import React from 'react';
import { MessagesSquare } from 'lucide-react';
import T from 'Components/Helpers/translate';
import type { InboxApprovalItem, InboxUpgradeRequest } from 'Resources/AssistantChat';
import { buildPath } from '../useWorkspaceNav';
import { actionLabelKey, capabilityLabel, entityLabel } from '../AgentConfig/autonomyGrants';

/**
 * What the agent is asking to be allowed to do, in the rep's words — "create
 * contacts", "send email" — and for how long. Falls back to a generic line when
 * the payload isn't a shape we recognise, so an odd row still reads as a
 * permission request.
 */
export const describeUpgrade = (upgrade: InboxUpgradeRequest | null | undefined): string => {
	let what: string;
	if (upgrade?.type === 'entity') {
		// Shared with the Permissions tab so the two name a grant the same way, and
		// so an upgrade on an alias key reads as `account` rather than the lang key.
		const action = T(actionLabelKey(upgrade.action));
		what = T('assistant.inbox.upgrade.entitySub', { action, entity: entityLabel(upgrade.entity) });
	} else if (upgrade?.type === 'capability') {
		what = T('assistant.inbox.upgrade.capabilitySub', { capability: capabilityLabel(upgrade.capability) });
	} else {
		what = T('assistant.inbox.upgrade.genericSub');
	}
	// WEB-11464: say what kind of yes this is. A permission holds until it is
	// taken back; a proposal says "this once".
	return `${what} — ${T('assistant.inbox.upgrade.scope')}`;
};

type Props = {
	item: InboxApprovalItem;
	onSelectConversation: (conversationId: number) => void;
};

/**
 * WEB-13219: the expanded body of a permission upgrade row. The request has no
 * conversation of its own — the upgrade tracker raises it after a streak of
 * clean approvals — so the row says what it grants in full, why it is being
 * asked, and links the threads where the streak was approved.
 */
const InboxUpgradeDetails = ({ item, onSelectConversation }: Props) => {
	const count = item.earned?.count;
	const conversations = item.earned?.conversations ?? [];
	return (
		<div className="ws-inbox-upgrade">
			<p>{describeUpgrade(item.upgrade)}</p>
			{count ? (
				<p className="ws-inbox-upgrade-why">{T('assistant.inbox.upgrade.earnedCount', { count })}</p>
			) : null}
			{conversations.length ? (
				<div className="ws-inbox-upgrade-earned">
					<span className="ws-inbox-upgrade-why">{T('assistant.inbox.upgrade.earnedIn')}</span>
					<ul>
						{conversations.map(c => (
							<li key={c.id}>
								<a
									className="ws-inbox-link ws-inbox-upgrade-thread"
									href={`#${buildPath({ route: 'chat', agentRoute: null, conversationId: c.id })}`}
									onClick={e => {
										e.preventDefault();
										onSelectConversation(c.id);
									}}
								>
									<MessagesSquare style={{ width: 14, height: 14 }} />
									<span>{c.title || T('assistant.chat')}</span>
								</a>
							</li>
						))}
					</ul>
				</div>
			) : null}
		</div>
	);
};

export default InboxUpgradeDetails;
